import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  Flex,
  useToast,
  Spinner,
  Text,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { FiUserPlus } from "react-icons/fi";

import UserService from "../UserRegistration/RegistrationUserService"; // Ajuste esta linha!
import OptionsService from "../UserRegistration/OptionsService";
import ModernPageShell from "../../components/layout/ModernPageShell";
import { getModernUi } from "../../components/layout/modernUi";

export default function UserRegistration({ modern = false }) {
  const ui = getModernUi(modern);
  const toast = useToast();

  const [formData, setFormData] = useState({
    login: "",
    password: "",
    name: "",
    position: null,
    status: null,
    role: null,
    company: null,
    // CORREÇÃO AQUI: removido .split("T")[0] para incluir a hora
    registerDate: new Date().toISOString(),
  });

  const [positions, setPositions] = useState([]);
  const [userStatuses, setUserStatuses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        const [
          employeesOptions,
          userStatusOptions,
          roleOptions,
          companyOptions,
        ] = await Promise.all([
          OptionsService.getOptionsByType("employees"),
          OptionsService.getOptionsByType("status"),
          OptionsService.getOptionsByType("role"),
          OptionsService.getOptionsByType("Company"), // Corrigido para "Company" com C maiúsculo
        ]);

        setPositions(employeesOptions);
        setUserStatuses(userStatusOptions);
        setRoles(roleOptions);
        setCompanies(companyOptions);
      } catch (error) {
        toast({
          title: "Erro ao carregar opções",
          description: "Não foi possível carregar as opções de cadastro.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        console.error("Erro ao buscar opções:", error);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "login") {
      setLoginError(""); // Limpa o erro de login ao digitar
    }
  };

  const handleOptionChange = (e, type) => {
    const selectedOptionId = e.target.value;
    let selectedOption = null;
    let description = "";
    let optionType = "";

    switch (type) {
      case "position":
        selectedOption = positions.find(
          (opt) => opt.optionsId === selectedOptionId
        );
        description = selectedOption ? selectedOption.description : "";
        optionType = "employees";
        break;
      case "status":
        selectedOption = userStatuses.find(
          (opt) => opt.optionsId === selectedOptionId
        );
        description = selectedOption ? selectedOption.description : "";
        optionType = "status";
        break;
      case "role":
        selectedOption = roles.find(
          (opt) => opt.optionsId === selectedOptionId
        );
        description = selectedOption ? selectedOption.description : "";
        optionType = "role";
        break;
      case "company":
        selectedOption = companies.find(
          (opt) => opt.optionsId === selectedOptionId
        );
        description = selectedOption ? selectedOption.description : "";
        optionType = "Company"; // Corrigido para "Company" com C maiúsculo
        break;
      default:
        break;
    }

    setFormData((prev) => ({
      ...prev,
      [type]: selectedOption
        ? {
            optionsId: selectedOption.optionsId,
            description: description,
            type: optionType,
          }
        : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError("");

    // Validação básica para garantir que todos os campos obrigatórios sejam preenchidos
    if (
      !formData.login ||
      !formData.password ||
      !formData.name ||
      !formData.position ||
      !formData.status ||
      !formData.role ||
      !formData.company
    ) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos obrigatórios.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        // Garante que o username seja o mesmo que o login, como seu backend espera
        username: formData.login,
        // Garante que estas propriedades booleanas sejam enviadas
        enabled: true,
        accountNonLocked: true,
        accountNonExpired: true,
        credentialsNonExpired: true,
      };

      const response = await UserService.createUser(payload);

      toast({
        title: "Sucesso!",
        description: "Usuário cadastrado com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Limpar o formulário após o sucesso
      setFormData({
        login: "",
        password: "",
        name: "",
        position: null,
        status: null,
        role: null,
        company: null,
        registerDate: new Date().toISOString(), // Resetar com a data atual formatada
      });
    } catch (error) {
      console.error(
        "Erro ao criar usuário:",
        error.response?.data || error.message
      );
      if (error.response && error.response.status === 409) {
        // Exemplo: 409 Conflict para login já existente
        setLoginError(
          "Este e-mail/login já está em uso. Por favor, escolha outro."
        );
      } else if (error.response && error.response.status === 400) {
        // Para erros de validação gerais do backend
        setLoginError(
          error.response.data.message ||
            "Erro de validação ao cadastrar usuário."
        );
      } else if (error.response && error.response.status === 403) {
        setLoginError(
          "Você não tem permissão para realizar esta operação. Sua sessão pode ter expirado. Por favor, faça login novamente."
        );
      } else {
        setLoginError(
          "Ocorreu um erro ao cadastrar o usuário. Tente novamente."
        );
      }
      toast({
        title: "Erro ao cadastrar",
        description:
          error.response?.data?.message ||
          "Ocorreu um erro ao cadastrar o usuário.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const commonInputStyles = {
    bg: "gray.50",
    borderColor: "gray.200",
    _focus: { borderColor: "red.500", boxShadow: "0 0 0 1px red.500" },
  };

  const formCard = (
    <Box
      rounded={modern ? "2xl" : "lg"}
      bg="white"
      boxShadow={modern ? "0 10px 30px rgba(15, 23, 42, 0.08)" : "lg"}
      border={modern ? "1px solid" : undefined}
      borderColor={modern ? "rgba(15, 23, 42, 0.06)" : undefined}
      p={8}
      maxW="md"
      width="full"
    >
      {!modern && (
        <Heading fontSize="2xl" mb={6} textAlign="center" color="gray.700">
          Cadastrar Novo Usuário
        </Heading>
      )}
        <form onSubmit={handleSubmit}>
          <FormControl id="login" mb={4} isRequired isInvalid={!!loginError}>
            <FormLabel>Login (E-mail)</FormLabel>
            <Input
              type="email"
              name="login"
              value={formData.login}
              onChange={handleChange}
              placeholder="Digite o e-mail do usuário"
              {...commonInputStyles}
            />
            <FormErrorMessage>{loginError}</FormErrorMessage>
          </FormControl>

          <FormControl id="password" mb={4} isRequired>
            <FormLabel>Senha</FormLabel>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Digite a senha"
              {...commonInputStyles}
            />
          </FormControl>

          <FormControl id="name" mb={4} isRequired>
            <FormLabel>Nome Completo</FormLabel>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Digite o nome completo"
              {...commonInputStyles}
            />
          </FormControl>

          <FormControl id="position" mb={4} isRequired>
            <FormLabel>Cargo</FormLabel>
            <Select
              name="position"
              placeholder={
                loadingOptions ? "Carregando cargos..." : "Selecione o Cargo"
              }
              value={formData.position ? formData.position.optionsId : ""}
              onChange={(e) => handleOptionChange(e, "position")}
              {...commonInputStyles}
              isDisabled={loadingOptions || isSubmitting}
              required
            >
              {loadingOptions ? (
                <option value="" disabled>
                  Carregando...
                </option>
              ) : (
                <>
                  <option value="">Selecione o Cargo</option>{" "}
                  {positions.map((opt) => (
                    <option key={opt.optionsId} value={opt.optionsId}>
                      {opt.description}
                    </option>
                  ))}
                </>
              )}
            </Select>
            {loadingOptions && (
              <Flex mt={2} align="center" justify="flex-start" color="gray.600">
                <Spinner size="sm" mr={2} />
                <Text fontSize="sm">Buscando opções de cargos...</Text>
              </Flex>
            )}
          </FormControl>

          <FormControl id="role" mb={4} isRequired>
            <FormLabel>Tipo de Acesso (Papel)</FormLabel>
            <Select
              name="role"
              placeholder={
                loadingOptions
                  ? "Carregando papéis..."
                  : "Selecione o Tipo de Acesso"
              }
              value={formData.role ? formData.role.optionsId : ""}
              onChange={(e) => handleOptionChange(e, "role")}
              {...commonInputStyles}
              isDisabled={loadingOptions || isSubmitting}
              required
            >
              {loadingOptions ? (
                <option value="" disabled>
                  Carregando...
                </option>
              ) : (
                <>
                  <option value="">Selecione o Tipo de Acesso</option>{" "}
                  {roles.map((opt) => (
                    <option key={opt.optionsId} value={opt.optionsId}>
                      {opt.description}
                    </option>
                  ))}
                </>
              )}
            </Select>
            {loadingOptions && (
              <Flex mt={2} align="center" justify="flex-start" color="gray.600">
                <Spinner size="sm" mr={2} />
                <Text fontSize="sm">Buscando opções de papéis...</Text>
              </Flex>
            )}
          </FormControl>

          <FormControl id="company" mb={4}>
            <FormLabel>Empresa</FormLabel>
            <Select
              name="company"
              placeholder={
                loadingOptions
                  ? "Carregando empresas..."
                  : "Selecione a Empresa"
              }
              value={formData.company ? formData.company.optionsId : ""}
              onChange={(e) => handleOptionChange(e, "company")}
              {...commonInputStyles}
              isDisabled={loadingOptions || isSubmitting}
              required
            >
              {loadingOptions ? (
                <option value="" disabled>
                  Carregando...
                </option>
              ) : (
                <>
                  <option value="">Selecione a Empresa</option>{" "}
                  {companies.map((opt) => (
                    <option key={opt.optionsId} value={opt.optionsId}>
                      {opt.description}
                    </option>
                  ))}
                </>
              )}
            </Select>
            {loadingOptions && (
              <Flex mt={2} align="center" justify="flex-start" color="gray.600">
                <Spinner size="sm" mr={2} />
                <Text fontSize="sm">Buscando opções de empresas...</Text>
              </Flex>
            )}
          </FormControl>

          <FormControl id="status" mb={4} isRequired>
            <FormLabel>Status</FormLabel>
            <Select
              name="status"
              placeholder={
                loadingOptions ? "Carregando status..." : "Selecione o Status"
              }
              value={formData.status ? formData.status.optionsId : ""}
              onChange={(e) => handleOptionChange(e, "status")}
              {...commonInputStyles}
              isDisabled={loadingOptions || isSubmitting}
              required
            >
              {loadingOptions ? (
                <option value="" disabled>
                  Carregando...
                </option>
              ) : (
                <>
                  <option value="">Selecione o Status</option>{" "}
                  {userStatuses.map((opt) => (
                    <option key={opt.optionsId} value={opt.optionsId}>
                      {opt.description}
                    </option>
                  ))}
                </>
              )}
            </Select>
            {loadingOptions && (
              <Flex mt={2} align="center" justify="flex-start" color="gray.600">
                <Spinner size="sm" mr={2} />
                <Text fontSize="sm">Buscando opções de status...</Text>
              </Flex>
            )}
          </FormControl>

          <Button
            type="submit"
            bg="red.600"
            color="white"
            _hover={{ bg: "red.700", boxShadow: "lg" }}
            size="lg"
            width="100%"
            isLoading={isSubmitting}
            spinnerPlacement="end"
            leftIcon={!isSubmitting && <FiUserPlus />}
            mt={4}
          >
            Cadastrar Usuário
          </Button>
        </form>
    </Box>
  );

  if (modern) {
    return (
      <ModernPageShell
        title="Cadastro de usuário"
        subtitle="Crie novos colaboradores com perfil, cargo e empresa."
        eyebrow="Administração"
        centered
        contentMaxW="md"
      >
        {formCard}
      </ModernPageShell>
    );
  }

  return (
    <Flex
      minH="calc(100vh - 80px)"
      align="center"
      justify="center"
      bg="gray.100"
      py={8}
      px={4}
    >
      {formCard}
    </Flex>
  );
}
